import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronDown, Mail, Lock, Globe, CalendarDays, Phone, Palette, GraduationCap, Shield, Type, MapPin, Bell, UserPlus, Star, Upload, Plus, Trash2, Briefcase, Award, Sparkles } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLang } from '../hooks/useLang';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import ThemeToggle from '../components/common/ThemeToggle';
import formatDate from '../utils/formatDate';
import { COUNTRIES, getCitiesForCountry } from '../utils/locations';

// user.location is stored as a single "City, Country" string (set once at
// signup as free text) — best-effort split so the two selects below start
// pre-filled instead of blank for existing users.
const parseLocation = (location) => {
  if (!location) return { country: '', city: '' };
  const parts = location.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const country = COUNTRIES.find((c) => c.toLowerCase() === parts[parts.length - 1].toLowerCase());
    if (country) return { country, city: parts.slice(0, -1).join(', ') };
  }
  const wholeMatch = COUNTRIES.find((c) => c.toLowerCase() === location.toLowerCase());
  if (wholeMatch) return { country: wholeMatch, city: '' };
  return { country: '', city: location };
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'az', label: 'Azərbaycan' },
  { code: 'ru', label: 'Русский' },
];

const DATE_FORMATS = [
  { code: 'dmy', label: 'Day.Month.Year' },
  { code: 'mdy', label: 'Month.Day.Year' },
  { code: 'long', label: 'Full date (e.g. November 23, 2026)' },
];

const EDUCATION_LEVELS = ["High School", "Bachelor's", "Master's", "PhD", "Other"];
const YEARS_OF_STUDY = ['1st Year', '2nd Year', '3rd Year', '4th Year', '5th Year', 'Graduate'];
const FONT_SIZES = [
  { code: 'sm', label: 'Small' },
  { code: 'md', label: 'Medium' },
  { code: 'lg', label: 'Large' },
];

const StatusLine = ({ status }) => {
  if (!status) return null;
  return (
    <p className={`text-xs mt-2 ${status.type === 'error' ? 'text-destructive' : 'text-primary'}`}>
      {status.message}
    </p>
  );
};

const inputClass = "w-full px-3 py-2 rounded-lg bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

// Every top-level setting used to be permanently expanded, so opening
// Settings just to change the language meant scrolling past email, phone and
// password fields sitting open the whole time. Now each category (and, for
// Security specifically, each item inside it) only shows its fields once
// clicked.
const CategoryCard = ({ id, icon: Icon, title, description, isOpen, onToggle, children }) => (
  <div id={id} className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden scroll-mt-24">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 p-5 text-left bg-transparent border-none cursor-pointer"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{title}</p>
          {description && <p className="text-[11px] text-muted-foreground truncate">{description}</p>}
        </div>
      </div>
      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          style={{ overflow: 'hidden' }}
        >
          <div className="px-5 pb-5 border-t border-border pt-5 space-y-5">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const SecurityRow = ({ icon: Icon, title, isOpen, onToggle, children }) => (
  <div className="rounded-xl border border-border overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left bg-muted/30 border-none cursor-pointer"
    >
      <span className="flex items-center gap-2 text-sm font-bold text-foreground">
        <Icon className="w-4 h-4 text-muted-foreground" /> {title}
      </span>
      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          style={{ overflow: 'hidden' }}
        >
          <div className="p-4 space-y-2">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// LinkedIn-style repeater: each entry is its own little card with a delete
// button, and "Add" appends a blank one. Used for education, projects and
// certificates, which are all lists of the same shape of thing.
const EntryList = ({ icon: Icon, title, description, entries, onAdd, onRemove, addLabel, emptyLabel, children }) => (
  <div className="rounded-xl border border-border overflow-hidden">
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-muted/30">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{title}</p>
          {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
        </div>
      </div>
      <button
        onClick={onAdd}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors border-none cursor-pointer flex-shrink-0"
      >
        <Plus className="w-3 h-3" /> {addLabel}
      </button>
    </div>
    <div className="p-4 space-y-3">
      {entries.length === 0 ? (
        <p className="text-[11px] text-muted-foreground text-center py-3">{emptyLabel}</p>
      ) : (
        entries.map((entry, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-xl bg-muted/40 p-3 pr-11 space-y-2"
          >
            {children(entry, index)}
            <button
              onClick={() => onRemove(index)}
              title="Remove"
              className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-background hover:bg-destructive/10 hover:text-destructive text-muted-foreground flex items-center justify-center border-none cursor-pointer transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        ))
      )}
    </div>
  </div>
);

const ToggleRow = ({ icon: Icon, title, description, checked, onChange }) => (
  <div className="flex items-center justify-between gap-3 py-1">
    <div className="flex items-center gap-3 min-w-0">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && <p className="text-[11px] text-muted-foreground truncate">{description}</p>}
      </div>
    </div>
    <button
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      className={`relative w-11 h-6 rounded-full flex-shrink-0 border-none cursor-pointer transition-colors ${checked ? 'bg-primary' : 'bg-muted'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  </div>
);

const Settings = () => {
  const { token, user, dispatch } = useAuthContext();
  const { language } = useLang();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const location = useLocation();
  const requestedCategory = new URLSearchParams(location.search).get('section');
  const [openCategory, setOpenCategory] = useState(requestedCategory);
  const toggleCategory = (id) => setOpenCategory((prev) => (prev === id ? null : id));

  // Arriving from a profile section ("Manage") should land on the right card
  // already open, and scrolled to.
  useEffect(() => {
    if (!requestedCategory) return;
    setOpenCategory(requestedCategory);
    const node = document.getElementById(`settings-${requestedCategory}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [requestedCategory]);

  const [openSecurityItem, setOpenSecurityItem] = useState(null);
  const toggleSecurityItem = (id) => setOpenSecurityItem((prev) => (prev === id ? null : id));

  const [fontSize, setFontSize] = useState(() => localStorage.getItem('fontSize') || 'md');
  useEffect(() => {
    document.documentElement.setAttribute('data-font-size', fontSize);
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  const [emailForm, setEmailForm] = useState(user?.email || '');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [phoneNumber, setPhoneNumber] = useState((user?.number || '').replace(/^\+/, ''));
  const [dateFormat, setDateFormat] = useState(user?.date_format || 'dmy');
  const [personalForm, setPersonalForm] = useState({
    university: user?.university || '',
    major: user?.major || '',
    education_level: user?.education_level || '',
    year_of_study: user?.year_of_study || '',
    ...parseLocation(user?.location),
  });
  const cityOptions = getCitiesForCountry(personalForm.country);

  // Education / projects / certificates / interests used to be edited from
  // the profile page itself; they live here now so there is one place that
  // owns "who I am" and the profile stays read-only.
  const [educations, setEducations] = useState(() => {
    const history = Array.isArray(user?.education_history) ? user.education_history : [];
    if (history.length) return history;
    // Seed the first row from the flat onboarding fields so existing accounts
    // don't start from an empty list.
    if (user?.university || user?.major || user?.education_level) {
      return [{
        institution: user.university || user.institution || '',
        level: user.education_level || '',
        major: user.major || '',
        year: user.year_of_study || '',
      }];
    }
    return [];
  });
  const [projects, setProjects] = useState(() => (Array.isArray(user?.projects) ? user.projects : []));
  const [certificates, setCertificates] = useState(() => (Array.isArray(user?.certificates) ? user.certificates : []));
  const [interests, setInterests] = useState(() => (user?.hobbies || []).join(', '));

  const updateEntry = (setter, index, field, value) =>
    setter((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  const addEntry = (setter, blank) => setter((prev) => [...prev, blank]);
  const removeEntry = (setter, index) => setter((prev) => prev.filter((_, i) => i !== index));

  const [savingField, setSavingField] = useState(null);
  const [status, setStatus] = useState({ email: null, password: null, phone: null, personal: null });
  const [notificationPrefs, setNotificationPrefs] = useState({
    follow_email: user?.notification_prefs?.follow_email !== false,
    rating_email: user?.notification_prefs?.rating_email !== false,
    new_upload_email: user?.notification_prefs?.new_upload_email !== false,
  });

  const updateUser = async (fields) => {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/auth/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(fields),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Update failed.');
    dispatch({ type: 'UPDATE_USER', payload: data.user });
    return data;
  };

  const handleSavePhone = async () => {
    setSavingField('phone');
    setStatus((prev) => ({ ...prev, phone: null }));
    try {
      await updateUser({ number: `+${phoneNumber}` });
      setStatus((prev) => ({ ...prev, phone: { type: 'success', message: 'Saved, but not verified yet.' } }));
    } catch (err) {
      setStatus((prev) => ({ ...prev, phone: { type: 'error', message: err.message } }));
    } finally {
      setSavingField(null);
    }
  };

  const handleDateFormatChange = async (newFormat) => {
    setDateFormat(newFormat);
    try {
      await updateUser({ date_format: newFormat });
    } catch (err) {
      console.error('Failed to save date format:', err);
    }
  };

  const handleSaveEmail = async () => {
    setSavingField('email');
    setStatus((prev) => ({ ...prev, email: null }));
    try {
      await updateUser({ email: emailForm });
      setStatus((prev) => ({ ...prev, email: { type: 'success', message: 'Email updated.' } }));
    } catch (err) {
      setStatus((prev) => ({ ...prev, email: { type: 'error', message: err.message } }));
    } finally {
      setSavingField(null);
    }
  };

  const handleSavePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setStatus((prev) => ({ ...prev, password: { type: 'error', message: "New passwords don't match." } }));
      return;
    }
    setSavingField('password');
    setStatus((prev) => ({ ...prev, password: null }));
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update password.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setStatus((prev) => ({ ...prev, password: { type: 'success', message: 'Password updated.' } }));
    } catch (err) {
      setStatus((prev) => ({ ...prev, password: { type: 'error', message: err.message } }));
    } finally {
      setSavingField(null);
    }
  };

  const handleToggleNotificationPref = async (key) => {
    const previous = notificationPrefs;
    const next = { ...notificationPrefs, [key]: !notificationPrefs[key] };
    setNotificationPrefs(next);
    try {
      await updateUser({ notification_prefs: next });
    } catch (err) {
      console.error('Failed to save notification preference:', err);
      setNotificationPrefs(previous);
    }
  };

  const handleSavePersonal = async () => {
    setSavingField('personal');
    setStatus((prev) => ({ ...prev, personal: null }));
    try {
      const { country, city } = personalForm;
      const locationValue = [city, country].filter(Boolean).join(', ');
      const cleanEducations = educations.filter((e) => (e.institution || '').trim() || (e.major || '').trim());
      // The flat university/major fields are still read in a few older places,
      // so keep them mirroring the first (most recent) education entry.
      const primaryEducation = cleanEducations[0] || {};
      const cleanProjects = projects.filter((p) => (p.title || '').trim());
      const cleanCertificates = certificates.filter((c) => (c.name || '').trim());
      const cleanInterests = interests.split(',').map((i) => i.trim()).filter(Boolean);
      await updateUser({
        university: primaryEducation.institution || '',
        major: primaryEducation.major || '',
        education_level: primaryEducation.level || '',
        year_of_study: primaryEducation.year || '',
        location: locationValue,
        educationHistory: cleanEducations,
        projects: cleanProjects,
        certificates: cleanCertificates,
        hobbies: cleanInterests,
      });
      setStatus((prev) => ({ ...prev, personal: { type: 'success', message: 'Saved.' } }));
    } catch (err) {
      setStatus((prev) => ({ ...prev, personal: { type: 'error', message: err.message } }));
    } finally {
      setSavingField(null);
    }
  };

  const handleLanguageChange = (newLang) => {
    navigate(`/${newLang}/settings`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-2xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate(`/${language}/dashboard`)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 bg-transparent border-none cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <h1 className="text-2xl font-bold text-foreground mb-6">Settings</h1>

        <div className="space-y-4">
          {/* Language & Region */}
          <CategoryCard
            id="settings-language"
            icon={Globe}
            title="Language & Region"
            description="Language and date format"
            isOpen={openCategory === 'language'}
            onToggle={() => toggleCategory('language')}
          >
            <div>
              <label className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" /> Language
              </label>
              <select className={inputClass} value={language} onChange={(e) => handleLanguageChange(e.target.value)}>
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">Full site translation is coming soon — this switches the URL for now.</p>
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-muted-foreground" /> Date Format
              </label>
              <select className={inputClass} value={dateFormat} onChange={(e) => handleDateFormatChange(e.target.value)}>
                {DATE_FORMATS.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">Preview: {formatDate(new Date(), dateFormat)}</p>
            </div>
          </CategoryCard>

          {/* Appearance */}
          <CategoryCard
            id="settings-appearance"
            icon={Palette}
            title="Appearance"
            description="Theme and text size"
            isOpen={openCategory === 'appearance'}
            onToggle={() => toggleCategory('appearance')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-foreground">Theme</p>
                <p className="text-[11px] text-muted-foreground">Currently {theme === 'light' ? 'Light' : 'Dark'}</p>
              </div>
              <ThemeToggle />
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <Type className="w-4 h-4 text-muted-foreground" /> Text Size
              </label>
              <div className="flex gap-2">
                {FONT_SIZES.map((f) => (
                  <button
                    key={f.code}
                    onClick={() => setFontSize(f.code)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border-none cursor-pointer transition-colors ${
                      fontSize === f.code ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </CategoryCard>

          {/* Personal Information */}
          <CategoryCard
            id="settings-personal"
            icon={GraduationCap}
            title="Personal Information"
            description="Education, projects, certificates, interests and location"
            isOpen={openCategory === 'personal'}
            onToggle={() => toggleCategory('personal')}
          >
            <EntryList
              icon={GraduationCap}
              title="Education"
              description="Where you study or studied"
              entries={educations}
              addLabel="Add"
              emptyLabel="No education added yet."
              onAdd={() => addEntry(setEducations, { institution: '', level: '', major: '', year: '' })}
              onRemove={(index) => removeEntry(setEducations, index)}
            >
              {(entry, index) => (
                <>
                  <input
                    className={inputClass}
                    value={entry.institution || ''}
                    onChange={(e) => updateEntry(setEducations, index, 'institution', e.target.value)}
                    placeholder="School or university"
                  />
                  <input
                    className={inputClass}
                    value={entry.major || ''}
                    onChange={(e) => updateEntry(setEducations, index, 'major', e.target.value)}
                    placeholder="Major / field of study"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className={inputClass}
                      value={entry.level || ''}
                      onChange={(e) => updateEntry(setEducations, index, 'level', e.target.value)}
                    >
                      <option value="">Level...</option>
                      {EDUCATION_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <select
                      className={inputClass}
                      value={entry.year || ''}
                      onChange={(e) => updateEntry(setEducations, index, 'year', e.target.value)}
                    >
                      <option value="">Year...</option>
                      {YEARS_OF_STUDY.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </>
              )}
            </EntryList>

            <EntryList
              icon={Briefcase}
              title="Projects & experience"
              description="Work you want people to see"
              entries={projects}
              addLabel="Add"
              emptyLabel="No projects added yet."
              onAdd={() => addEntry(setProjects, { title: '', year: '', description: '', link: '' })}
              onRemove={(index) => removeEntry(setProjects, index)}
            >
              {(entry, index) => (
                <>
                  <div className="grid grid-cols-[1fr_100px] gap-2">
                    <input
                      className={inputClass}
                      value={entry.title || ''}
                      onChange={(e) => updateEntry(setProjects, index, 'title', e.target.value)}
                      placeholder="Project or role"
                    />
                    <input
                      className={inputClass}
                      value={entry.year || ''}
                      onChange={(e) => updateEntry(setProjects, index, 'year', e.target.value)}
                      placeholder="Year"
                    />
                  </div>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={2}
                    value={entry.description || ''}
                    onChange={(e) => updateEntry(setProjects, index, 'description', e.target.value)}
                    placeholder="What was it about?"
                  />
                  <input
                    className={inputClass}
                    value={entry.link || ''}
                    onChange={(e) => updateEntry(setProjects, index, 'link', e.target.value)}
                    placeholder="Link (optional)"
                  />
                </>
              )}
            </EntryList>

            <EntryList
              icon={Award}
              title="Certificates"
              description="Courses and credentials you earned"
              entries={certificates}
              addLabel="Add"
              emptyLabel="No certificates added yet."
              onAdd={() => addEntry(setCertificates, { name: '', issuer: '', year: '', url: '' })}
              onRemove={(index) => removeEntry(setCertificates, index)}
            >
              {(entry, index) => (
                <>
                  <input
                    className={inputClass}
                    value={entry.name || ''}
                    onChange={(e) => updateEntry(setCertificates, index, 'name', e.target.value)}
                    placeholder="Certificate name"
                  />
                  <div className="grid grid-cols-[1fr_100px] gap-2">
                    <input
                      className={inputClass}
                      value={entry.issuer || ''}
                      onChange={(e) => updateEntry(setCertificates, index, 'issuer', e.target.value)}
                      placeholder="Issued by"
                    />
                    <input
                      className={inputClass}
                      value={entry.year || ''}
                      onChange={(e) => updateEntry(setCertificates, index, 'year', e.target.value)}
                      placeholder="Year"
                    />
                  </div>
                  <input
                    className={inputClass}
                    value={entry.url || ''}
                    onChange={(e) => updateEntry(setCertificates, index, 'url', e.target.value)}
                    placeholder="Credential link (optional)"
                  />
                </>
              )}
            </EntryList>

            <div>
              <label className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-muted-foreground" /> Interests
              </label>
              <input
                className={inputClass}
                value={interests}
                onChange={(e) => setInterests(e.target.value)}
                placeholder="e.g. Chemistry, Robotics, Chess"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Comma-separated. We use these to recommend materials you might like.
              </p>
              {interests.trim() && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {interests.split(',').map((i) => i.trim()).filter(Boolean).map((tag, idx) => (
                    <span key={idx} className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-medium">{tag}</span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" /> Location
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  className={inputClass}
                  value={personalForm.country}
                  onChange={(e) => setPersonalForm((prev) => ({ ...prev, country: e.target.value, city: '' }))}
                >
                  <option value="">Country...</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <AnimatePresence mode="wait" initial={false}>
                  {cityOptions.length > 0 ? (
                    <motion.select
                      key="city-select"
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 6 }}
                      transition={{ duration: 0.18 }}
                      className={inputClass}
                      value={personalForm.city}
                      onChange={(e) => setPersonalForm((prev) => ({ ...prev, city: e.target.value }))}
                    >
                      <option value="">City...</option>
                      {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </motion.select>
                  ) : (
                    <motion.input
                      key="city-input"
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 6 }}
                      transition={{ duration: 0.18 }}
                      className={inputClass}
                      value={personalForm.city}
                      onChange={(e) => setPersonalForm((prev) => ({ ...prev, city: e.target.value }))}
                      placeholder={personalForm.country ? "City..." : "Select a country first"}
                      disabled={!personalForm.country}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
            <button
              onClick={handleSavePersonal}
              disabled={savingField === 'personal'}
              className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50 border-none cursor-pointer"
            >
              {savingField === 'personal' ? 'Saving...' : 'Save'}
            </button>
            <StatusLine status={status.personal} />
          </CategoryCard>

          {/* Security */}
          <CategoryCard
            id="settings-security"
            icon={Shield}
            title="Security"
            description="Email, phone number, and password"
            isOpen={openCategory === 'security'}
            onToggle={() => toggleCategory('security')}
          >
            <div className="space-y-2">
              <SecurityRow icon={Mail} title="Email" isOpen={openSecurityItem === 'email'} onToggle={() => toggleSecurityItem('email')}>
                <div className="flex gap-2">
                  <input
                    className={inputClass}
                    type="email"
                    value={emailForm}
                    onChange={(e) => setEmailForm(e.target.value)}
                    placeholder="you@example.com"
                  />
                  <button
                    onClick={handleSaveEmail}
                    disabled={savingField === 'email'}
                    className="px-4 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50 flex-shrink-0 border-none cursor-pointer"
                  >
                    {savingField === 'email' ? '...' : 'Save'}
                  </button>
                </div>
                <StatusLine status={status.email} />
              </SecurityRow>

              <SecurityRow icon={Phone} title="Phone Number" isOpen={openSecurityItem === 'phone'} onToggle={() => toggleSecurityItem('phone')}>
                <div className="flex gap-2 items-start settings-phone-input">
                  <PhoneInput
                    country={'az'}
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    specialLabel=""
                    inputStyle={{ width: '100%' }}
                  />
                  <button
                    onClick={handleSavePhone}
                    disabled={savingField === 'phone'}
                    className="px-4 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50 flex-shrink-0 border-none cursor-pointer"
                    style={{ height: '38px' }}
                  >
                    {savingField === 'phone' ? '...' : 'Save'}
                  </button>
                </div>
                {user?.number_verified === false && !status.phone && (
                  <p className="text-xs mt-2 text-muted-foreground">Saved, but not verified yet.</p>
                )}
                <StatusLine status={status.phone} />
              </SecurityRow>

              <SecurityRow icon={Lock} title="Password" isOpen={openSecurityItem === 'password'} onToggle={() => toggleSecurityItem('password')}>
                <div className="space-y-2">
                  <input
                    className={inputClass}
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Current password"
                  />
                  <input
                    className={inputClass}
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="New password"
                  />
                  <input
                    className={inputClass}
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                  />
                  <button
                    onClick={handleSavePassword}
                    disabled={savingField === 'password' || !passwordForm.currentPassword || !passwordForm.newPassword}
                    className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50 border-none cursor-pointer"
                  >
                    {savingField === 'password' ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
                <StatusLine status={status.password} />
              </SecurityRow>
            </div>
          </CategoryCard>

          <CategoryCard
            id="settings-notifications"
            icon={Bell}
            title="Notifications"
            description="Choose what YeniDers emails you about"
            isOpen={openCategory === 'notifications'}
            onToggle={() => toggleCategory('notifications')}
          >
            <div className="space-y-3">
              <ToggleRow
                icon={UserPlus}
                title="New followers"
                description="Email me when someone starts following me"
                checked={notificationPrefs.follow_email}
                onChange={() => handleToggleNotificationPref('follow_email')}
              />
              <ToggleRow
                icon={Star}
                title="Ratings"
                description="Email me when someone rates my material"
                checked={notificationPrefs.rating_email}
                onChange={() => handleToggleNotificationPref('rating_email')}
              />
              <ToggleRow
                icon={Upload}
                title="New uploads from people I follow"
                description="Email me when someone I follow uploads something new"
                checked={notificationPrefs.new_upload_email}
                onChange={() => handleToggleNotificationPref('new_upload_email')}
              />
              <p className="text-[11px] text-muted-foreground pt-1">In-app notifications (the bell icon) always stay on so you never miss activity on your account.</p>
            </div>
          </CategoryCard>
        </div>
      </main>
    </div>
  );
};

export default Settings;
